import React from 'react';
import { Commission } from '../types/commission';
import { DollarSign, FileText, Briefcase, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
interface AdminSummaryMetricsProps {
  data: Commission[];
  onClickFlagged: () => void;
}
export function AdminSummaryMetrics({
  data,
  onClickFlagged
}: AdminSummaryMetricsProps) {
  const totalCommission = data.reduce((acc, curr) => acc + curr.total, 0);
  const totalUnits = data.reduce((acc, curr) => acc + curr.units, 0);
  const totalLoanVolume = data.reduce((acc, curr) => {
    return acc + curr.loans.reduce((loanAcc, loan) => loanAcc + loan.amount, 0);
  }, 0);
  const flaggedCount = data.filter(c => c.review?.status === 'flagged' || c.review?.status === 'under_review').length;
  const metrics = [{
    label: 'Total Commission',
    value: `$${totalCommission.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`,
    icon: DollarSign,
    gradient: 'from-emerald-500 to-teal-500',
    valueColor: 'text-emerald-400'
  }, {
    label: 'Total Units',
    value: totalUnits,
    icon: FileText,
    gradient: 'from-blue-500 to-indigo-500',
    valueColor: 'text-white'
  }, {
    label: 'Total Loan Volume',
    value: `$${totalLoanVolume.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`,
    icon: Briefcase,
    gradient: 'from-indigo-500 to-purple-500',
    valueColor: 'text-white'
  }];
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-2">
      {metrics.map((metric, index) => <motion.div key={metric.label} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.1
    }} className="bg-navy-800/70 backdrop-blur-sm rounded-xl border border-white/5 p-4 shadow-sm" data-id="element-3">
          <div className="flex items-center gap-2 mb-2" data-id="element-4">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${metric.gradient} shadow-sm`} data-id="element-5">
              <metric.icon size={18} className="text-white" data-id="element-6" />
            </div>
            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider" data-id="element-7">
              {metric.label}
            </p>
          </div>
          <p className={`text-xl font-bold ${metric.valueColor} tracking-tight`} data-id="element-8">
            {metric.value}
          </p>
        </motion.div>)}

      {/* Flagged Commissions Tile */}
      <motion.button initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} onClick={onClickFlagged} disabled={flaggedCount === 0} className={`bg-navy-800/70 backdrop-blur-sm rounded-xl border-2 p-4 shadow-sm transition-all ${flaggedCount > 0 ? 'border-amber-500/30 hover:border-amber-500/50 hover:shadow-md hover:shadow-amber-500/5 cursor-pointer' : 'border-white/5 opacity-50 cursor-not-allowed'}`} data-id="element-9">
        <div className="flex items-center gap-2 mb-2" data-id="element-10">
          <div className={`p-2 rounded-lg ${flaggedCount > 0 ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-slate-700'} shadow-sm`} data-id="element-11">
            <AlertCircle size={18} className="text-white" data-id="element-12" />
          </div>
          <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider text-left" data-id="element-13">
            Flagged Commissions
          </p>
        </div>
        <p className={`text-xl font-bold tracking-tight ${flaggedCount > 0 ? 'text-amber-400' : 'text-slate-500'}`} data-id="element-14">
          {flaggedCount}
        </p>
      </motion.button>
    </div>;
}